import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useEntityAction } from "@/lib/mutations";
import { Button, ErrorNote } from "@/components/ui";
import { Labeled, Select, TextField } from "@/components/Form";
import { agentDraftIssues, type AgentDraft } from "@/lib/bookingAgent";
import type { TravelAgent } from "@/lib/types";

const NEW = "__new__";

/** The agent dropdown; "+ new agent" asks the parent to open the inline form. */
function AgentSelect({
  value,
  onChange,
  onAddNew,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  onAddNew: () => void;
}) {
  const { t } = useTranslation();
  const agents = useQuery({
    queryKey: ["travel-agents"],
    queryFn: () => api.get<TravelAgent[]>("/travel-agents"),
  });
  return (
    <Select
      value={value ?? ""}
      onChange={(e) => {
        if (e.target.value === NEW) onAddNew();
        else onChange(e.target.value || null);
      }}
    >
      <option value="">{t("bookingAgent.choose")}</option>
      {(agents.data ?? []).map((a) => (
        <option key={a.id} value={a.id}>
          {a.name}
          {a.phone ? ` · ${a.phone}` : ""}
        </option>
      ))}
      <option value={NEW}>+ {t("bookingAgent.newAgent")}</option>
    </Select>
  );
}

/** Add a travel agent on the spot (name + phone); the new agent is selected. */
function NewAgentForm({
  onCreated,
  onCancel,
}: {
  onCreated: (id: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [nameMissing, setNameMissing] = useState(false);
  const create = useEntityAction([["travel-agents"]], { inlineError: true });
  return (
    <div className="space-y-2 rounded-md border border-olive-600 bg-olive-800 p-3 sm:col-span-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Labeled
          label={t("bookingAgent.newName")}
          required
          invalid={nameMissing}
          error={t("bookingAgent.nameRequired")}
        >
          <TextField value={name} onChange={(e) => setName(e.target.value)} />
        </Labeled>
        <Labeled label={t("fields.phone")}>
          <TextField value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+971…" />
        </Labeled>
      </div>
      {create.isError && <ErrorNote message={(create.error as Error).message} />}
      <div className="flex gap-2">
        <Button
          type="button"
          className="px-3 py-1.5 text-xs"
          loading={create.isPending}
          onClick={() => {
            if (!name.trim()) {
              setNameMissing(true);
              return;
            }
            create.mutate(
              () =>
                api.post<TravelAgent>("/travel-agents", {
                  name: name.trim(),
                  phone: phone.trim() || null,
                }),
              { onSuccess: (agent) => onCreated((agent as TravelAgent).id) },
            );
          }}
        >
          {t("bookingAgent.addAgent")}
        </Button>
        <button
          type="button"
          className="text-sm text-sand-muted hover:text-sand disabled:opacity-50"
          disabled={create.isPending}
          onClick={onCancel}
        >
          {t("common.cancel")}
        </button>
      </div>
    </div>
  );
}

/** «Откуда клиент»: Напрямую / От турагента — and, for an agent, who and what
 * they earn (a fixed amount or a percent of the sale). Owner rule 7: what is
 * still blank glows once the form has been tried (`tried`). */
export function BookingAgentFields({
  value,
  onChange,
  currency,
  tried,
}: {
  value: AgentDraft;
  onChange: (patch: Partial<AgentDraft>) => void;
  currency: string;
  tried: boolean;
}) {
  const { t } = useTranslation();
  const issues = agentDraftIssues(value);
  const fromAgent = value.origin === "agent";
  const [adding, setAdding] = useState(false);
  return (
    <>
      <Labeled label={t("bookingAgent.origin")}>
        <Select
          value={value.origin}
          onChange={(e) => onChange({ origin: e.target.value as AgentDraft["origin"] })}
        >
          <option value="direct">{t("bookingAgent.direct")}</option>
          <option value="agent">{t("bookingAgent.fromAgent")}</option>
        </Select>
      </Labeled>
      {fromAgent && (
        <>
          <Labeled
            label={t("bookingAgent.agent")}
            required
            invalid={tried && issues.agent}
            error={t("bookingAgent.agentRequired")}
          >
            <AgentSelect
              value={value.agentId}
              onChange={(id) => onChange({ agentId: id })}
              onAddNew={() => setAdding(true)}
            />
          </Labeled>
          {adding && (
            <NewAgentForm
              onCreated={(id) => {
                onChange({ agentId: id });
                setAdding(false);
              }}
              onCancel={() => setAdding(false)}
            />
          )}
          <Labeled label={t("bookingAgent.commissionType")}>
            <Select
              value={value.type}
              onChange={(e) => onChange({ type: e.target.value as AgentDraft["type"] })}
            >
              <option value="percent">{t("bookingAgent.percent")}</option>
              <option value="fixed">{t("bookingAgent.fixed")}</option>
              <option value="none">{t("bookingAgent.none")}</option>
            </Select>
          </Labeled>
          {value.type !== "none" && (
            <Labeled
              label={
                value.type === "percent"
                  ? t("bookingAgent.percentValue")
                  : t("bookingAgent.fixedValue", { currency })
              }
              required
              invalid={tried && issues.value}
              error={
                value.type === "percent"
                  ? t("bookingAgent.percentRequired")
                  : t("bookingAgent.fixedRequired")
              }
            >
              <TextField
                inputMode="decimal"
                value={value.value}
                onChange={(e) => onChange({ value: e.target.value })}
                placeholder={value.type === "percent" ? "0" : "0.00"}
              />
            </Labeled>
          )}
          {/* owner rule 18 — said where the terms are entered, not discovered in the numbers */}
          <p className="text-xs text-sand-muted sm:col-span-3">{t("bookingAgent.rule")}</p>
        </>
      )}
    </>
  );
}
